import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import defaultSkinTone from './defaultSkinTone';

export function hideMeshMaterials(meshData: GLTF) {
  meshData.scene.traverse((child) => {
    if (child instanceof THREE.Mesh && child.type === 'SkinnedMesh') {
      if (child.material.name.includes('Hide')) {
        child.material.visible = false;
      }
      if (
        child.material.isMaterial &&
        child.geometry.type === 'BufferGeometry'
      ) {
        // TODO: Subdivision modifier would go here. ThreeJS has poor support for it though
        // Tried to use https://github.com/stevinz/three-subdivide and it seemed to not work well for
        // modifying an existing mesh
      }
    }
    if (child.name.endsWith('_RND')) {
      child.visible = false;
    }
  });
}

export function turnOnShadows(meshData: GLTF) {
  meshData.scene.traverse((object: THREE.Object3D) => {
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

export function disableFrustumCulled(meshData: GLTF) {
  // Disable frustumCulled for children meant to be seen.
  // https://discourse.threejs.org/t/parts-of-glb-object-disappear-in-certain-angles-and-zoom/21295/4
  meshData.scene.traverse((child) => {
    if ('frustumCulled' in child) {
      if (child instanceof THREE.Mesh && child.material.name.includes('Hide')) {
        child.visible = false;
      } else {
        child.frustumCulled = false;
      }
    }
  });
}

export function showSkeleton(meshData: GLTF) {
  const rootBone = meshData.scene.getObjectByName('root');
  if (rootBone) {
    const skeletonHelper = new THREE.SkeletonHelper(rootBone);
    (skeletonHelper.material as THREE.LineBasicMaterial).color =
      new THREE.Color(0x000000);
    meshData.scene.add(skeletonHelper);
  } else {
    console.warn('Could not find the root bone to show skeleton of');
  }
}

export function applySkinHex(skinHex?: string, charUploadID?: string) {
  const hexString = defaultSkinTone(skinHex, charUploadID);
  const hexNumber = hexStringToNumber(hexString);

  return (meshData: GLTF) => {
    if (!meshData) return;
    meshData.scene.traverse((child) => {
      if (
        child instanceof THREE.Mesh &&
        child.isMesh &&
        'material' in child &&
        ['Body', 'Face'].includes(child.material.name)
      ) {
        child.material.color.setHex(hexNumber);
        child.material.metalness = 0;
      }
    });
  };
}

export function applyFaceExpression(faceExpression: string) {
  return (meshData: GLTF) => {
    if (!meshData) return;
    const texturePath = `/images/static_faces_textures/${faceExpression}.png`;
    meshData.scene.traverse((child) => {
      if (
        child instanceof THREE.Mesh &&
        child.isMesh &&
        'material' in child &&
        child.material.name === 'Face'
      ) {
        // Axel doesn't have face expressions
        if (child.parent?.name.includes('Axel')) {
          return;
        }
        new THREE.TextureLoader().load(texturePath, (tex) => {
          // Override default Three.js behavior of Y-flipping textures on load
          tex.flipY = false;
          child.material.map = tex;
          child.material.needsUpdate = true;
        });
      }
    });
  };
}

export function applyShowSkeleton(meshData: GLTF, showSkeleton: boolean) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    if (child.name.endsWith('_geo')) {
      child.visible = !showSkeleton;
    }
    if (child.name.endsWith('_skl')) {
      child.visible = showSkeleton;
    }
  });
}

export function applyShowRenderingCharacter(
  meshData: GLTF,
  showRendering: boolean,
) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    if (child.name.endsWith('_geo')) {
      child.visible = !showRendering;
    }
    if (child.name.endsWith('_skl')) {
      child.visible = !showRendering;
    }
    if (child.name.endsWith('_RND')) {
      child.visible = showRendering;
    }
  });
}

function hexStringToNumber(hexString: string) {
  return parseInt(hexString.replace('#', ''), 16);
}
